import React, { PureComponent } from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import * as yup from 'yup';
import { Formik } from 'formik';
import LazyLoad from 'react-lazyload';

import { Button, Image, Select } from 'shared/lib/SureDesignSystem';
import Footer from '../Footer';
import Reviews from './Reviews';
import FAQSection from '../FAQSection';

import step1 from './assets/steps/step1.png';
import step2 from './assets/steps/step2.png';
import step3 from './assets/steps/step3.png';

import step1Webp from './assets/steps/step1.webp';
import step2Webp from './assets/steps/step2.webp';
import step3Webp from './assets/steps/step3.webp';

import farmers from './assets/partners/farmers.png';
import chubb from './assets/partners/chubb.png';
import assurant from './assets/partners/assurant.png';
import berkley from './assets/partners/berkley.png';
import nationwide from './assets/partners/nationwide.png';

import farmersWebp from './assets/partners/farmers.webp';
import chubbWebp from './assets/partners/chubb.webp';
import assurantWebp from './assets/partners/assurant.webp';
import berkleyWebp from './assets/partners/berkley.webp';
import nationwideWebp from './assets/partners/nationwide.webp';

import avatar1 from './assets/reviews/avatar1.png';
import avatar2 from './assets/reviews/avatar2.png';
import avatar3 from './assets/reviews/avatar3.png';

import avatar1Webp from './assets/reviews/avatar1.webp';
import avatar2Webp from './assets/reviews/avatar2.webp';
import avatar3Webp from './assets/reviews/avatar3.webp';

import { isWebPSupported } from 'shared/lib/utils';

import styles from './Landing.module.css';

const insuranceOptions = [
  {
    label: 'Personal',
    value: 'personal',
  },
  {
    label: 'Business',
    value: 'business',
  },
  {
    label: 'Travel',
    value: 'travel',
  },
];

const categoryOptionsGroups = {
  personal: [
    {
      label: 'Renters',
      value: 'renters-insurance',
    },
  ],
  business: [
    {
      label: 'Small Business',
      value: 'small-business-owners-insurance',
    },
  ],
  travel: [
    {
      label: 'Baggage',
      value: 'baggage-protection',
    },
    {
      label: 'Rental Car',
      value: 'rental-car-insurance',
    },
  ],
};

const propTypes = {
  navTo: PropTypes.func,
};

const defaultProps = {
  navTo: () => {},
};

class Landing extends PureComponent {
  handleSubmit = values => {
    if (values.category === 'renters-insurance') {
      window.location.assign(
        `https://www.${
          process.env.REACT_APP_AMTRUST_DOMAIN
        }.com/product/amtrust/amtrust-renters-direct/get-started`
      );
    } else {
      values.category && this.props.navTo(values.category);
    }
  };

  handleInitialValidation = ({ initialValues }) => {
    return this.validationSchema.isValidSync(initialValues);
  };

  get initialFormVals() {
    return {
      insurance: '',
      category: '',
    };
  }

  get validationSchema() {
    return yup.object({
      insurance: yup.string().required('Select a type of insurance'),
      category: yup.string().when('insurance', {
        is: '',
        then: yup.string().nullable(),
        otherwise: yup.string().required('Select a category'),
      }),
    });
  }

  render() {
    return (
      <div className={styles.wrapper}>
        <div
          className={classnames(
            styles.heroSection,
            isWebPSupported
              ? styles.heroSectionBackgroundWebp
              : styles.heroSectionBackground
          )}
        >
          <div className={styles.heroSection_container}>
            <div className={styles.heroSection_titleWrapper}>
              <h1 className={styles.heroSection_title}>
                Insurance coverage for every amazing thing in your life.
              </h1>
              <Formik
                onSubmit={this.handleSubmit}
                ref={c => (this.formik = c)}
                initialValues={this.initialFormVals}
                isInitialValid={this.handleInitialValidation}
                validationSchema={this.validationSchema}
              >
                {({
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  values,
                  errors,
                  touched,
                }) => (
                  <form
                    className={styles.leadGenSection_form}
                    onSubmit={handleSubmit}
                  >
                    <div className={styles.heroSection_selections}>
                      <Select
                        name="insurance"
                        placeholder="Select Insurance"
                        options={insuranceOptions}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.insurance}
                        error={errors.insurance}
                        touched={touched.insurance}
                        wrapperClassName={styles.heroSection_select}
                      />
                      <Select
                        name="category"
                        placeholder="Category"
                        options={
                          values.insurance
                            ? categoryOptionsGroups[values.insurance]
                            : []
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.category}
                        error={errors.category}
                        touched={touched.category}
                        wrapperClassName={classnames(
                          styles.heroSection_select,
                          values.insurance
                            ? ''
                            : styles.heroSection_select_hidden
                        )}
                      />
                    </div>
                    <Button type="submit" className={styles.heroSection_btn}>
                      Check our prices
                    </Button>
                  </form>
                )}
              </Formik>
            </div>
          </div>
        </div>

        <div className={styles.partnersSection}>
          <div className={styles.partnersSection_container}>
            <div className={styles.partnersSection_item_wrapper}>
              <LazyLoad height={62} once>
                <Image
                  src={farmers}
                  webp={farmersWebp}
                  className={styles.partnersSection_item}
                  alt="Farmers logo"
                />
              </LazyLoad>
            </div>
            <div className={styles.partnersSection_item_wrapper}>
              <LazyLoad height={62} once>
                <Image
                  src={chubb}
                  webp={chubbWebp}
                  className={styles.partnersSection_item}
                  alt="Chubb logo"
                />
              </LazyLoad>
            </div>
            <div className={styles.partnersSection_item_wrapper}>
              <LazyLoad height={62} once>
                <Image
                  src={assurant}
                  webp={assurantWebp}
                  className={styles.partnersSection_item}
                  alt="Assurant logo"
                />
              </LazyLoad>
            </div>
            <div className={styles.partnersSection_item_wrapper}>
              <LazyLoad height={62} once>
                <Image
                  src={berkley}
                  webp={berkleyWebp}
                  className={styles.partnersSection_item}
                  alt="Berkley logo"
                />
              </LazyLoad>
            </div>
            <div className={styles.partnersSection_item_wrapper}>
              <LazyLoad height={62} once>
                <Image
                  src={nationwide}
                  webp={nationwideWebp}
                  className={styles.partnersSection_item}
                  alt="Nationwide logo"
                />
              </LazyLoad>
            </div>
          </div>
        </div>

        <div className={styles.stepsSection}>
          <div className={styles.stepsSection_container}>
            <h2 className={styles.stepsSection_title}>
              We make it look easy <span>(because it is.)</span>
            </h2>
            <p className={styles.stepsSection_subTitle}>Here’s how it works:</p>

            <div className={styles.stepsSection_stepsWrapper}>
              <div
                className={classnames(
                  styles.stepsSection_step,
                  styles.stepsSection_step1
                )}
              >
                <div className={styles.stepsSection_stepPatternWrapper}>
                  <LazyLoad height={80} once>
                    <Image
                      src={step1}
                      webp={step1Webp}
                      className={styles.stepsSection_stepPattern}
                    />
                  </LazyLoad>
                </div>

                <div className={styles.stepsSection_stepContent}>
                  <h3 className={styles.stepsSection_stepTitle}>Step 1</h3>
                  <p className={styles.stepsSection_stepDescription}>
                    Customize the highest quality insurance offerings from the
                    most trusted carriers.
                  </p>
                </div>
              </div>
              <div
                className={classnames(
                  styles.stepsSection_step,
                  styles.stepsSection_step2
                )}
              >
                <div className={styles.stepsSection_stepPatternWrapper}>
                  <LazyLoad height={80} once>
                    <Image
                      src={step2}
                      webp={step2Webp}
                      className={styles.stepsSection_stepPattern}
                    />
                  </LazyLoad>
                </div>

                <div className={styles.stepsSection_stepContent}>
                  <h3 className={styles.stepsSection_stepTitle}>Step 2</h3>
                  <p className={styles.stepsSection_stepDescription}>
                    Instantly purchase and receive your digital policy.
                  </p>
                </div>
              </div>
              <div
                className={classnames(
                  styles.stepsSection_step,
                  styles.stepsSection_step3
                )}
              >
                <div className={styles.stepsSection_stepPatternWrapper}>
                  <LazyLoad height={80} once>
                    <Image
                      src={step3}
                      webp={step3Webp}
                      className={styles.stepsSection_stepPattern}
                    />
                  </LazyLoad>
                </div>

                <div className={styles.stepsSection_stepContent}>
                  <h3 className={styles.stepsSection_stepTitle}>Step 3</h3>
                  <p className={styles.stepsSection_stepDescription}>
                    Manage everything from anywhere.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.perksSection}>
          <div className={styles.perksSection_container}>
            <h2 className={styles.perksSection_perksTitle}>The Perks</h2>

            <div className={styles.perksSection_perksItems}>
              <div className={styles.perksSection_perkItem}>
                <div className={styles.perksSection_perkContent}>
                  <h3 className={styles.perksSection_perkTitle}>
                    Paperless Experience
                  </h3>
                  <p className={styles.perksSection_perkDescription}>
                    No more waiting on policy forms—instantly download your docs
                    as soon as you bind coverage.
                  </p>
                </div>
              </div>
              <div className={styles.perksSection_perkItem}>
                <div className={styles.perksSection_perkContent}>
                  <h3 className={styles.perksSection_perkTitle}>
                    Report a Claim
                  </h3>
                  <p className={styles.perksSection_perkDescription}>
                    Submit your claims online without having to mail in forms or
                    talk to an agent (unless you’d like to). Plus, get
                    reimbursed right to your bank account.
                  </p>
                </div>
              </div>
              <div className={styles.perksSection_perkItem}>
                <div className={styles.perksSection_perkContent}>
                  <h3 className={styles.perksSection_perkTitle}>
                    Policy Management
                  </h3>
                  <p className={styles.perksSection_perkDescription}>
                    You’re in control, managing your policies easily and on the
                    fly.
                  </p>
                </div>
              </div>
              <div className={styles.perksSection_perkItem}>
                <div className={styles.perksSection_perkContent}>
                  <h3 className={styles.perksSection_perkTitle}>
                    Bank-Grade Security
                  </h3>
                  <p className={styles.perksSection_perkDescription}>
                    All personal information and transactions are secure and
                    encrypted using the industry’s best practices. And, we’ll
                    never share your information with a third party.
                  </p>
                </div>
              </div>
              <div className={styles.perksSection_perkItem}>
                <div className={styles.perksSection_perkContent}>
                  <h3 className={styles.perksSection_perkTitle}>
                    Mobile Access Anywhere
                  </h3>
                  <p className={styles.perksSection_perkDescription}>
                    Buy new policies or edit existing ones right from the palm
                    of your hand.
                  </p>
                </div>
              </div>
              <div className={styles.perksSection_perkItem}>
                <div className={styles.perksSection_perkContent}>
                  <h3 className={styles.perksSection_perkTitle}>
                    Available Around the World
                  </h3>
                  <p className={styles.perksSection_perkDescription}>
                    SURE is currently available across 3 continents, 50 states
                    and thousands of cities, with coverage expansions happening
                    all the time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Reviews
          items={[
            {
              id: 1,
              review:
                'I was meeting some friends in Colorado for a ski trip when I noticed one of my bags was missing from the claim area. Of course it was the bag containing my new ski boots. The airline never found my bag, but baggage protection paid me enough to get newer, better boots and some new skis, no questions asked.',
              author: 'Margot M.',
              location: 'Santa Barbara, CA',
              avatar: avatar1,
              avatarWebp: avatar1Webp,
            },
            {
              id: 2,
              review:
                'My wife and I arrived to our Bali honeymoon down one piece of luggage (hers). Given the weather, she didn’t have anything appropriate to wear until we were able to track down the bag. Having someone on our side to deal with the airlines allowed us to enjoy our first day in peace, despite the wrench in our plans.',
              author: 'Antonio C.',
              location: 'Queens, NY',
              avatar: avatar2,
              avatarWebp: avatar2Webp,
            },
            {
              id: 3,
              review:
                'As I was scrambling my way to the gate, I remembered that I hadn’t gotten protection for my luggage for this particular trip–something I always do. While the plane was idling on the runway, I was able to purchase protection and get coverage. Better late than never!',
              author: 'Danielle B.',
              location: 'Boulder, CO',
              avatar: avatar3,
              avatarWebp: avatar3Webp,
            },
          ]}
        />

        <div
          className={classnames(
            styles.leadGenSection,
            isWebPSupported
              ? styles.leadGenSectionBackgroundWebp
              : styles.leadGenSectionBackground
          )}
        >
          <div className={styles.leadGenSection_container}>
            <h2 className={styles.leadGenSection_title}>
              Looking to Partner or use the Platform?
            </h2>
            <Link to="/contact">
              <Button className={styles.leadGenSection_button} secondary>
                Learn more
              </Button>
            </Link>
          </div>
        </div>

        <FAQSection />

        <Footer />
      </div>
    );
  }
}

Landing.propTypes = propTypes;
Landing.defaultProps = defaultProps;

export default Landing;
